'use client';

import { useEffect } from 'react';
import { Palette, Clock, Users, TrendingUp } from 'lucide-react';
import { SitePage, PageHero, Section, Card, IconBadge, Btn } from '@/components/site/page-kit';

export default function ForInteriorDesignersPage() {
  useEffect(() => { window.scrollTo({ top: 0, behavior: 'instant' }); }, []);

  return (
    <SitePage>
      <PageHero
        eyebrow="For Interior Designers"
        title="HINCH for Interior Designers"
        description={<>
          HINCH empowers interior designers with a centralized platform to discover 10,000+ material designs, 
          compare finishes across brands, access detailed specifications, and connect directly with verified 
          suppliers for seamless project execution across India.
        </>}
      />

      <Section tone="white">
        <div className="grid gap-3 md:grid-cols-2 md:gap-4">
          {[
            {icon: Palette, title: "Massive Material Library", desc: "10,000+ laminate designs, 500+ hardware options, 200+ panel varieties with advanced filters"},
            {icon: Clock, title: "Time-Saving Tools", desc: "Quick comparison, specification downloads, sample ordering, and direct supplier connectivity"},
            {icon: Users, title: "Client Collaboration", desc: "Create material boards, save favorites, share with clients, and track project orders"},
            {icon: TrendingUp, title: "Verified Suppliers", desc: "Connect with verified manufacturers, get competitive pricing, bulk discounts, and pan-India delivery"}
          ].map((item, idx) => (
            <div key={idx} className="rounded-2xl border border-hv-line bg-hv-paper p-6 md:p-8">
              <div className="mb-5"><IconBadge icon={item.icon} /></div>
              <h3 className="mb-2 text-xl">{item.title}</h3>
              <p className="leading-relaxed text-hv-soft">{item.desc}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section title="How Interior Designers Use HINCH">
        <div className="grid gap-3 md:grid-cols-3 md:gap-4">
          {[
            { title: 'Residential Projects', desc: 'Source materials for kitchens, bedrooms, living rooms, and complete home interiors with curated design collections.' },
            { title: 'Commercial Projects', desc: 'Find durable, high-traffic materials for offices, retail spaces, restaurants, and hospitality projects.' },
            { title: 'Custom Furniture Design', desc: 'Procure specialized laminates, hardware, and finishes for bespoke furniture manufacturing.' },
          ].map((use, idx) => (
            <Card key={use.title}>
              <div className="mb-4 font-hv-mono text-[13px] text-hv-orange">0{idx + 1}</div>
              <h3 className="mb-2 text-lg">{use.title}</h3>
              <p className="leading-relaxed text-hv-soft">{use.desc}</p>
            </Card>
          ))}
        </div>

        <div className="mt-10 flex flex-col items-start gap-6 rounded-[20px] bg-hv-yellow md:mt-14 p-6 md:flex-row md:items-center md:justify-between md:p-11">
          <div>
            <h2 className="mb-2 text-[26px] leading-[1.1] tracking-[-0.02em] md:text-[36px]">Join 5,000+ Designers Using HINCH</h2>
            <p className="text-hv-ink/80">Start sourcing materials faster with India&apos;s largest interior materials platform.</p>
          </div>
          <Btn href="/" kind="dark" arrow>Explore Platform</Btn>
        </div>
      </Section>
    </SitePage>
  );
}
